import { Checkbox, SegmentedButtons, Typography } from '@scale/scaleui';
import { ReviewLevel } from '@scale/shared-types';
import _ from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { InteractionStepType } from '@scale/shared/types/chat';
import { REVIEW_LEVELS } from './common';
import { useProjectId } from 'react-scale/corp/views/CorpProjectGroupManager/hooks/useProjectId';
import { DeprecatedFeature } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ChatConfigurationEditor/components/ConfigLifeCycleInfo';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';

enum VisibilityOption {
  None = 'none',
  Restricted = 'restricted',
}

export function ReviewLevelConfig({
  currentParams,
  setCurrentParams,
}: {
  currentParams: any;
  setCurrentParams: (value: any) => void;
}) {
  const visibilityOption = useMemo(() => {
    if (!_.isNil(currentParams.params.visibility_restricted)) {
      return VisibilityOption.Restricted;
    } else {
      return VisibilityOption.None;
    }
  }, [currentParams]);

  const projectId = useProjectId();
  const hideExcludeFromCustomerView = useUnleashFeatureFlag(
    'chat-v1-remove-exclude-from-customer-view',
    { projectId },
    false,
  );

  // This is temporary state so that we don't lose the previous values when switching
  // between different visibility options
  const [defaultVisibilityRestricted, setDefaultVisibilityRestricted] = useState<ReviewLevel[]>(
    currentParams.params.visibility_restricted || REVIEW_LEVELS.map(r => r.level),
  );

  const selectVisibilityOption = useCallback(
    (option: VisibilityOption) => {
      const params = currentParams.params;
      const newParams = { ...params };

      // Save the current visibility options in case we go back
      if (visibilityOption === VisibilityOption.Restricted) {
        setDefaultVisibilityRestricted(params.visibility_restricted);
      }
      // In order to keep the config clean: fully delete previous options when switching modes
      if (option === VisibilityOption.None) {
        delete newParams.visibility_restricted;
        setCurrentParams({
          ...currentParams,
          params: newParams,
        });
      } else if (option === VisibilityOption.Restricted) {
        setCurrentParams({
          ...currentParams,
          params: {
            visibility_restricted: defaultVisibilityRestricted,
            ...newParams,
          },
        });
      }
    },
    [visibilityOption, currentParams, setCurrentParams, defaultVisibilityRestricted],
  );

  const hideFromCustomer = useMemo<boolean>(() => {
    return currentParams.params.hide_from_customer_view || false;
  }, [currentParams]);

  const visibleLevels = currentParams.params.visibility_restricted;
  const readOnlyLevels = currentParams.params.readonly_review_levels;
  const type = currentParams.type as InteractionStepType;

  const toggleVisibleLevel = useCallback(
    (level: ReviewLevel) => {
      let newVisibleLevels: ReviewLevel[] = [...visibleLevels];
      if (newVisibleLevels.includes(level)) {
        newVisibleLevels = newVisibleLevels.filter(l => l !== level);
      } else {
        newVisibleLevels.push(level);
      }
      setCurrentParams({
        ...currentParams,
        params: {
          ...currentParams.params,
          visibility_restricted: newVisibleLevels,
        },
      });
    },
    [visibleLevels, currentParams, setCurrentParams],
  );

  const toggleEditableLevel = useCallback(
    (level: ReviewLevel) => {
      let newReadOnlyLevels: ReviewLevel[] = readOnlyLevels ? [...readOnlyLevels] : [];
      if (newReadOnlyLevels.includes(level)) {
        newReadOnlyLevels = newReadOnlyLevels.filter(l => l !== level);
      } else {
        newReadOnlyLevels.push(level);
      }
      setCurrentParams({
        ...currentParams,
        params: {
          ...currentParams.params,
          readonly_review_levels: newReadOnlyLevels,
        },
      });
    },
    [readOnlyLevels, currentParams, setCurrentParams],
  );

  const visibleReadOnlyLevels = useMemo(
    () =>
      REVIEW_LEVELS.filter(
        opts =>
          opts.level !== ReviewLevel.ReviewConsensus &&
          (visibilityOption !== VisibilityOption.Restricted || visibleLevels.includes(opts.level)),
      ),
    [visibilityOption, visibleLevels],
  );

  const isConsensusEditableConfigIrrelevant = useMemo(() => {
    const possibleConsensusEditableTypes = [
      InteractionStepType.ModelResponseSelector,
      InteractionStepType.TextCollection,
      InteractionStepType.TextCollectionPerResponse,
      InteractionStepType.ModelResponseRanking,
      InteractionStepType.ModelResponseEditor,
    ];
    if (!possibleConsensusEditableTypes.includes(type)) return true;

    if (visibilityOption === VisibilityOption.Restricted) {
      return !visibleLevels.includes(ReviewLevel.ReviewConsensus);
    }
  }, [visibilityOption, visibleLevels, type]);

  const isConsensusEditable = useMemo(() => {
    return currentParams.params.editable_during_consensus;
  }, [currentParams]);

  const toggleHideFromCustomer = useCallback(() => {
    setCurrentParams({
      ...currentParams,
      params: {
        ...currentParams.params,
        hide_from_customer_view: !hideFromCustomer,
      },
    });
  }, [currentParams, hideFromCustomer, setCurrentParams]);

  const toggleConsensusEditable = useCallback(() => {
    if (!isConsensusEditableConfigIrrelevant) {
      setCurrentParams({
        ...currentParams,
        params: {
          ...currentParams.params,
          editable_during_consensus: !currentParams.params.editable_during_consensus,
        },
      });
    }
  }, [isConsensusEditableConfigIrrelevant, currentParams, setCurrentParams]);

  return (
    <>
      <div className="flex flex-col gap-4">
        <Typography.Subtitle2>Review Level Visibility</Typography.Subtitle2>
        <div className="flex gap-4">
          <SegmentedButtons
            buttons={[
              {
                icon: ['far', 'users'],
                id: VisibilityOption.None,
                label: 'Always visible',
              },
              {
                icon: ['far', 'user-edit'],
                id: VisibilityOption.Restricted,
                label: 'Restrict review level visibility',
              },
            ]}
            onSelect={id => selectVisibilityOption(id as VisibilityOption)}
            selected={visibilityOption}
          />
        </div>

        <div>
          {visibilityOption === VisibilityOption.None && (
            <div className="prose text-sm mb-4 italic">
              This step will be visible to all review levels.
            </div>
          )}

          {visibilityOption === VisibilityOption.Restricted && (
            <div className="mb-4">
              <div className="prose text-sm italic mb-3">
                If any of these boxes are checked, then this step will only be visible on review
                levels that are checked.
              </div>
              <div className="flex flex-col">
                {REVIEW_LEVELS.map(opts => (
                  <Checkbox
                    key={'visible-level-' + opts.level}
                    id={opts.level.toString()}
                    label={opts.name}
                    checked={visibleLevels?.includes(opts.level)}
                    onChange={() => toggleVisibleLevel(opts.level)}
                  />
                ))}
              </div>
            </div>
          )}

          {!hideExcludeFromCustomerView && (
            <DeprecatedFeature
              name="Exclude from Customer View"
              details="Please use the Customer Visibility tab to hide this step from customers."
              showInfoCondition={!!hideFromCustomer}
            >
              <div className="flex flex-col gap-2 mb-4">
                <Checkbox
                  id="exclude-customer-view"
                  className="font-weight-bold"
                  label="Exclude from Customer View"
                  disabled={false}
                  checked={hideFromCustomer}
                  onChange={toggleHideFromCustomer}
                />
                <div className="prose text-sm italic ml-8">
                  When selected, this step will not be visible to the customer and will not appear
                  in the API response.
                </div>
              </div>
            </DeprecatedFeature>
          )}

          {visibleReadOnlyLevels.length > 0 && (
            <div className="flex flex-col gap-2 mb-4">
              <Typography.Subtitle2>Read-Only Review Levels</Typography.Subtitle2>
              <div className="prose text-sm italic">
                If checked, review levels will not be able to edit this step. Note that sometimes
                other logic will make this step readonly even when not selected here (e.g. during
                customer audits).
              </div>
              <div className="flex flex-col gap-2">
                {visibleReadOnlyLevels.map(opts => (
                  <Checkbox
                    key={'read-only-level-' + opts.level}
                    id={opts.level.toString()}
                    label={opts.name}
                    checked={readOnlyLevels?.includes(opts.level)}
                    onChange={() => toggleEditableLevel(opts.level)}
                  />
                ))}
              </div>
            </div>
          )}

          {!isConsensusEditableConfigIrrelevant && (
            <div className="flex flex-col gap-2 mb-4">
              <Checkbox
                id="consensus-editable"
                className="font-weight-bold"
                label="Make This Step Editable in L4"
                checked={isConsensusEditable}
                onChange={toggleConsensusEditable}
              />
              <div className="prose text-sm italic ml-8">
                If checked, taskers at L4 will be able to make changes to this step. This is off by
                default because most steps should be non-editable at L4 unless they are specifically
                used for consensus.
              </div>
              {type === InteractionStepType.ModelResponseEditor && (
                <div className="prose text-sm italic ml-8">
                  For this step, there is no consensus strategy applied. This step should only be
                  editable at L4 if there is only one attempt.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
